import axios from 'axios';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, DataTable, Empty, Input, Modal, Select, Spin, StatCard, useAlert } from '../ui';
import { DataTableColumn } from '../ui/DataTable';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import User from '../../Models/User';
import datas from "../../Assets/data.json";
import env from 'react-dotenv';
import { Crypter } from '../../Functions/Security/Crypter';
import { statsHub } from '../../Services/signalr';
import PageHeader from '../ui/PageHeader';
import { AreaChart, BarChart, Card, DonutChart, LineChart, Subtitle, Title } from '@tremor/react';
import { ArrowLeftRight, Activity, Ban, BarChart3, CheckCircle2, ConciergeBell, Filter, Globe, Hourglass, LayoutList, PhoneCall, RefreshCw, Timer, Users, UserX, XCircle } from 'lucide-react';

/** Canaux de prise de ticket — options fixes. */
const CANAUX = [
    { value: '', label: 'Tous les canaux' },
    { value: 'ONLINE', label: 'En ligne (ONLINE)' },
    { value: 'MOBILE', label: 'Mobile (MOBILE)' },
    { value: 'WEB', label: 'Web (WEB)' },
    { value: 'USSD', label: 'USSD' },
    { value: 'RDV', label: 'Rendez-vous (RDV)' },
    { value: 'BORNE', label: 'Borne (BORNE)' },
];

/** Les 7 jeux de résultats attendus + les jeux supplémentaires détectés dynamiquement. */
interface StatSets {
    globaux: any[];
    parService: any[];
    parCanal: any[];
    parStatut: any[];
    parAgent: any[];
    tempsMoyens: any[];
    serie: any[];
    extras: { name: string; rows: any[] }[];
}

const EMPTY_SETS: StatSets = {
    globaux: [], parService: [], parCanal: [], parStatut: [],
    parAgent: [], tempsMoyens: [], serie: [], extras: [],
};

/** Ordre positionnel des jeux quand Result est un tableau de tableaux (ou Table/Table1/...). */
const POSITIONAL_KEYS: (keyof Omit<StatSets, 'extras'>)[] = [
    'globaux', 'parService', 'parCanal', 'parStatut', 'parAgent', 'tempsMoyens', 'serie',
];

/** Clés nommées recherchées quand Result est un objet. */
const NAMED_KEYS: { key: keyof Omit<StatSets, 'extras'>; candidates: RegExp[] }[] = [
    { key: 'globaux', candidates: [/^glob/i, /counter/i, /compteur/i, /total/i] },
    { key: 'parService', candidates: [/service/i] },
    { key: 'parCanal', candidates: [/canal/i, /canaux/i] },
    { key: 'parStatut', candidates: [/statut/i, /status/i] },
    { key: 'parAgent', candidates: [/agent/i, /caissi/i, /operat/i] },
    { key: 'tempsMoyens', candidates: [/temps/i] },
    { key: 'serie', candidates: [/jour/i, /serie/i, /date/i, /daily/i] },
];

/** Force une valeur en tableau. */
function asArray(v: any): any[] {
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') return [v];
    return [];
}

/**
 * Normalise res.Result en 7 jeux de résultats.
 * - Tableau de tableaux → indexé par position (globaux, parService, parCanal, parStatut, parAgent, tempsMoyens, serie).
 * - Objet → clés nommées (Globaux/Compteurs, ParService, ParCanal, ParStatut, ParAgent, TempsMoyens, SerieJournaliere/ParJour)
 *   ou dataset .NET (Table, Table1, ...).
 * - Forme inconnue → jeux détectés affichés dynamiquement dans `extras`.
 */
function extractSets(result: any): StatSets {
    const sets: StatSets = { ...EMPTY_SETS, extras: [] };
    let r = result;
    if (typeof r === 'string') {
        try { r = JSON.parse(r); } catch { return sets; }
    }
    if (!r) return sets;

    // Cas 1 : tableau
    if (Array.isArray(r)) {
        if (r.length > 0 && r.every((e: any) => Array.isArray(e))) {
            // Tableau de jeux → positionnel
            r.forEach((rows: any, i: number) => {
                if (i < POSITIONAL_KEYS.length) {
                    (sets as any)[POSITIONAL_KEYS[i]] = asArray(rows);
                } else {
                    sets.extras.push({ name: `Jeu de données ${i + 1}`, rows: asArray(rows) });
                }
            });
        } else if (r.length > 0) {
            // Tableau plat d'objets : un seul jeu de données, affiché tel quel
            sets.extras.push({ name: 'Données', rows: r });
        }
        return sets;
    }

    // Cas 2 : objet
    if (typeof r === 'object') {
        const keys = Object.keys(r);
        const tableKeys = keys
            .filter(k => /^Table\d*$/.test(k))
            .sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }));

        if (tableKeys.length > 0) {
            // Dataset .NET : Table, Table1, Table2... → positionnel
            tableKeys.forEach((k, i) => {
                if (i < POSITIONAL_KEYS.length) {
                    (sets as any)[POSITIONAL_KEYS[i]] = asArray(r[k]);
                } else {
                    sets.extras.push({ name: k, rows: asArray(r[k]) });
                }
            });
            keys.filter(k => !/^Table\d*$/.test(k)).forEach(k => {
                const v = asArray(r[k]);
                if (v.length) sets.extras.push({ name: k, rows: v });
            });
            return sets;
        }

        // Clés nommées
        const consumed = new Set<string>();
        NAMED_KEYS.forEach(nk => {
            const found = keys.find(k => !consumed.has(k) && nk.candidates.some(p => p.test(k)));
            if (found) {
                consumed.add(found);
                (sets as any)[nk.key] = asArray(r[found]);
            }
        });
        keys.filter(k => !consumed.has(k)).forEach(k => {
            const v = asArray(r[k]);
            if (v.length) sets.extras.push({ name: k, rows: v });
        });
        return sets;
    }

    return sets;
}

interface Counter { key: string; label: string; value: any }

/** Métadonnées d'affichage des compteurs connus (libellé + icône + ton + description). */
const COUNTER_META: Record<string, { label: string; icon: ReactNode; tone: 'primary' | 'success' | 'danger' | 'warning' | 'neutral'; description: string }> = {
    NbAppeles: {
        label: 'Tickets appelés', icon: <PhoneCall size={20} />, tone: 'primary',
        description: 'Nombre de tickets appelés au guichet sur la période. Chaque appel correspond à un client invité à se présenter au box.'
    },
    NbValides: {
        label: 'Tickets validés', icon: <CheckCircle2 size={20} />, tone: 'success',
        description: 'Tickets dont le service a été mené à terme (statut VALIDE). C\'est l\'indicateur principal de production : plus il est élevé, plus la file est bien absorbée.'
    },
    NbRejetes: {
        label: 'Tickets rejetés', icon: <XCircle size={20} />, tone: 'danger',
        description: 'Tickets refusés au guichet (statut REJETÉ) : dossier incomplet, critères non remplis, opération impossible. Un taux élevé peut signaler un problème de qualité des demandes en amont.'
    },
    NbAbsents: {
        label: 'Clients absents', icon: <UserX size={20} />, tone: 'warning',
        description: 'Clients appelés mais ne s\'étant pas présentés au guichet (no-show). Un rappel peut les remettre en file ; sinon le ticket passe en ABSENT.'
    },
    NbAnnules: {
        label: 'Tickets annulés', icon: <Ban size={20} />, tone: 'neutral',
        description: 'Tickets annulés par le client ou purgés automatiquement (check-in jamais confirmé en fin de journée).'
    },
    NbTransferes: {
        label: 'Tickets transférés', icon: <ArrowLeftRight size={20} />, tone: 'primary',
        description: 'Tickets réorientés vers un autre service (statut TRANSFÉRÉ) — ex. client arrivé au mauvais guichet. Beaucoup de transferts peuvent indiquer un mauvais routage des opérations.'
    },
    NbEnAttente: {
        label: 'En attente', icon: <Hourglass size={20} />, tone: 'warning',
        description: 'Tickets actuellement présents dans la file d\'attente, en attente d\'appel. Reflète la charge instantanée.'
    },
    TempsMoyenSec: {
        label: 'Temps moyen de service', icon: <Timer size={20} />, tone: 'neutral',
        description: 'Durée moyenne de traitement d\'un ticket au guichet (appel → validation). Plus il est bas, plus le service est fluide.'
    },
    TempsMoyenAttenteSec: {
        label: "Temps moyen d'attente", icon: <Timer size={20} />, tone: 'neutral',
        description: 'Durée moyenne entre l\'arrivée du client (check-in / création du ticket) et son appel au guichet. Indicateur clé de l\'expérience client.'
    },
    TempsMoyenServiceSec: {
        label: 'Temps moyen de service', icon: <Timer size={20} />, tone: 'neutral',
        description: 'Durée moyenne de traitement d\'un ticket au guichet (appel → validation). Plus il est bas, plus le service est fluide.'
    },
};

/**
 * Construit la liste des compteurs globaux.
 * - Variante « ligne de totaux » : [{NbAppeles: 10, NbValides: 8, ...}] → chaque clé numérique = un compteur.
 * - Variante « liste d'indicateurs » : [{Indicateur: 'NbAppeles', Valeur: 10}, ...] → chaque ligne = un compteur.
 */
function buildCounters(globaux: any[]): Counter[] {
    if (!globaux.length) return [];
    const first = globaux[0];
    if (!first || typeof first !== 'object') return [];
    const keys = Object.keys(first);

    const kLabel = keys.find(k => /indic|libell?e|nom|label|metric/i.test(k));
    const kVal = keys.find(k => /valeur|value|total|^nb|count|montant/i.test(k));
    if (kLabel && kVal && kLabel !== kVal && keys.length <= 4) {
        return globaux
            .filter(r => r && r[kLabel] != null)
            .map(r => ({ key: String(r[kLabel]), label: String(r[kLabel]), value: r[kVal] }));
    }

    return keys
        .filter(k => {
            const v = first[k];
            return v != null && v !== '' && typeof v !== 'object' && !isNaN(Number(v));
        })
        .map(k => ({ key: k, label: k, value: first[k] }));
}

/** Formate la valeur d'un compteur (les durées en secondes deviennent "X min Y s"). */
function formatCounterValue(c: Counter): string {
    if (/temps/i.test(c.key) && /sec/i.test(c.key)) {
        const s = Number(c.value);
        if (!isNaN(s)) {
            const m = Math.floor(s / 60);
            return m > 0 ? `${m} min ${Math.round(s % 60)} s` : `${Math.round(s)} s`;
        }
    }
    return c.value != null && c.value !== '' ? String(c.value) : '—';
}

/** Première clé d'un objet dont le nom matche l'une des expressions. */
function findKey(obj: any, patterns: RegExp[]): string | undefined {
    if (!obj || typeof obj !== 'object') return undefined;
    return Object.keys(obj).find(k => patterns.some(p => p.test(k)));
}

/** Formate une date de série en libellé court jj/mm. */
function formatJour(v: any): string {
    if (v == null || v === '') return '';
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Mappe la série journalière vers le format Tremor ({Jour, Tickets}) — clés tolérantes Date/Jour + Nb/Total. */
function buildSerie(rows: any[]): any[] {
    return rows
        .map(r => {
            const kDate = findKey(r, [/^date/i, /jour/i, /day/i, /periode/i]) || Object.keys(r)[0];
            const kVal = findKey(r, [/^nb/i, /total/i, /count/i, /ticket/i]);
            if (!kDate) return null;
            return { Jour: formatJour(r[kDate]), Tickets: kVal ? (Number(r[kVal]) || 0) : 0 };
        })
        .filter((r): r is { Jour: any; Tickets: number } => r !== null && r.Jour !== '');
}

/** Colonnes dynamiques construites depuis les clés de la première ligne (hors objets et clé technique id). */
function dynColumns(rows: any[]): DataTableColumn[] {
    const keys: string[] = [];
    rows.slice(0, 20).forEach(r => {
        if (!r || typeof r !== 'object') return;
        Object.keys(r).forEach(k => {
            if (k !== 'id' && !keys.includes(k) && (r[k] == null || typeof r[k] !== 'object')) keys.push(k);
        });
    });
    return keys.map(k => ({ field: k, headerName: k }));
}

/** Ajoute une clé technique id aux lignes pour le DataTable. */
function withId(rows: any[]): any[] {
    return rows.map((r, i) => ({ id: r?.Id ?? r?.id ?? i, ...r }));
}

/* ------------------------------------------------------------------ */
/*  Carte indicateur : graphique <-> données, métrique et type        */
/*  de graphe choisissables, description affichée.                    */
/* ------------------------------------------------------------------ */

/** Détecte la colonne « libellé » d'un jeu (catégorie du graphique). */
function detectLabelKey(rows: any[]): string | undefined {
    const r = rows[0];
    if (!r || typeof r !== 'object') return undefined;
    const keys = Object.keys(r).filter(k => k !== 'id' && r[k] != null && typeof r[k] !== 'object');
    return keys.find(k => typeof r[k] === 'string' && isNaN(Number(r[k])))
        || keys.find(k => /libelle|nom|service|canal|statut|agent|label|code/i.test(k))
        || keys[0];
}

/** Colonnes numériques exploitables comme métrique. */
function detectValueKeys(rows: any[]): string[] {
    const r = rows[0];
    if (!r || typeof r !== 'object') return [];
    return Object.keys(r).filter(k =>
        k !== 'id' && r[k] != null && r[k] !== '' && !isNaN(Number(r[k])) && typeof r[k] !== 'object'
    );
}

/** Choisit la meilleure métrique par défaut (Nb*, Total*, Tickets, Durée…). */
function bestValueKey(keys: string[]): string | undefined {
    return keys.find(k => /^nb|total|count|ticket|duree|temps|valeur|montant/i.test(k)) || keys[0];
}

const CHART_COLORS = ['indigo', 'cyan', 'emerald', 'amber', 'rose', 'violet', 'blue', 'fuchsia'];

const segBtn = 'px-2 py-1 text-[11px] font-medium transition-colors';
const segActive = 'bg-[var(--eb-primary)] text-white';
const segIdle = 'text-[var(--eb-text-muted)] hover:bg-[var(--eb-bg)]';

function IndicatorCard(props: {
    title: string;
    description: string;
    icon: ReactNode;
    rows: any[];
    /** 'bar' | 'donut' | 'line' — type de graphe par défaut. */
    defaultChart: 'bar' | 'donut' | 'line';
    /** Métriques affichables en "min X s" si la clé contient Temps/Sec. */
}) {
    const [view, setView] = useState<'chart' | 'data'>('chart');
    const [chartType, setChartType] = useState<'bar' | 'donut' | 'line'>(props.defaultChart);
    const [metric, setMetric] = useState<string>('');

    const labelKey = detectLabelKey(props.rows);
    const valueKeys = detectValueKeys(props.rows);
    const effectiveMetric = metric || bestValueKey(valueKeys) || '';

    const chartData = useMemo(() => {
        if (!labelKey || !effectiveMetric) return [];
        const groups: Record<string, number> = {};
        props.rows.forEach(r => {
            const k = String(r[labelKey] ?? '—');
            groups[k] = (groups[k] || 0) + (Number(r[effectiveMetric]) || 0);
        });
        return Object.keys(groups)
            .map(k => ({ name: k, value: Math.round(groups[k] * 100) / 100 }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 12);
    }, [props.rows, labelKey, effectiveMetric]);

    const fmt = (v: number) =>
        /temps|duree|sec/i.test(effectiveMetric) && v >= 60
            ? `${Math.floor(v / 60)}m${Math.round(v % 60)}s`
            : v.toLocaleString('fr-FR');

    return (
        <div className="eb-card flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-start gap-2.5 min-w-0">
                    <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-[var(--eb-primary-soft)] text-[var(--eb-primary)] flex items-center justify-center">
                        {props.icon}
                    </span>
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-[var(--eb-text)]">{props.title}</h3>
                        <p className="text-[11px] text-[var(--eb-text-muted)] leading-snug mt-0.5">{props.description}</p>
                    </div>
                </div>
                {/* Bascule Graphique / Données */}
                <div className="flex rounded-lg border border-[var(--eb-border)] overflow-hidden flex-shrink-0">
                    <button className={`${segBtn} ${view === 'chart' ? segActive : segIdle}`} onClick={() => setView('chart')} title="Vue graphique">
                        <BarChart3 size={13} />
                    </button>
                    <button className={`${segBtn} ${view === 'data' ? segActive : segIdle}`} onClick={() => setView('data')} title="Voir les données">
                        <LayoutList size={13} />
                    </button>
                </div>
            </div>

            {view === 'chart' ? (
                <>
                    {/* Choix du type de graphe + métrique */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="flex rounded-md border border-[var(--eb-border)] overflow-hidden">
                            {([['bar', 'Barres'], ['donut', 'Donut'], ['line', 'Courbe']] as const).map(([t, l]) => (
                                <button key={t} className={`${segBtn} ${chartType === t ? segActive : segIdle}`} onClick={() => setChartType(t)}>{l}</button>
                            ))}
                        </div>
                        {valueKeys.length > 1 && (
                            <select
                                value={effectiveMetric}
                                onChange={(e) => setMetric(e.target.value)}
                                className="text-[11px] px-2 py-1 rounded-md border border-[var(--eb-border)] bg-[var(--eb-surface)] text-[var(--eb-text)] focus:outline-none"
                            >
                                {valueKeys.map(k => <option key={k} value={k}>{k}</option>)}
                            </select>
                        )}
                    </div>
                    {chartData.length === 0 ? (
                        <div className="py-10 text-center text-xs text-[var(--eb-text-muted)]">Pas de données graphables</div>
                    ) : chartType === 'donut' ? (
                        <DonutChart data={chartData} index="name" category="value" className="h-56" colors={CHART_COLORS as any} valueFormatter={fmt} />
                    ) : chartType === 'line' ? (
                        <LineChart data={chartData} index="name" categories={['value']} className="h-56" colors={['indigo']} yAxisWidth={48} valueFormatter={fmt} />
                    ) : (
                        <BarChart data={chartData} index="name" categories={['value']} className="h-56" colors={['indigo']} yAxisWidth={48} valueFormatter={fmt} />
                    )}
                </>
            ) : (
                <DataTable
                    rows={withId(props.rows)}
                    columns={dynColumns(props.rows)}
                    height={240}
                    searchable={false}
                    emptyText="Aucune donnée"
                />
            )}
        </div>
    );
}

export default function ContenuStat() {

    // USER TOKEN
    const authHeader = useAuthHeader()

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const alert = useAlert()

    const [loading, setLoading] = useState(false)
    const [sets, setSets] = useState<StatSets | null>(null)
    const [selectedCounter, setSelectedCounter] = useState<Counter | null>(null)

    // Filtres
    const [filiales, setFiliales] = useState<any[]>([])
    const [services, setServices] = useState<any[]>([])
    const [filtreFiliale, setFiltreFiliale] = useState('')
    const [filtreService, setFiltreService] = useState('')
    const [filtreCanal, setFiltreCanal] = useState('')
    const [dateDebut, setDateDebut] = useState('')
    const [dateFin, setDateFin] = useState('')

    useEffect(() => {
        LoadFiliales()
        LoadServices()
        LoadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // SignalR /hubs/stats — compteurs live + signal de refresh débouncé serveur
    useEffect(() => {
        const getJwt = () => (authHeader() || '').replace(/^Bearer\s+/i, '')
        statsHub.acquire(getJwt, { codeAbonne: user.CodeAbonne, codeFiliale: user.CodeFiliale }).catch(() => { })

        const unsubs = [
            // Incrément local — pas de requête HTTP
            statsHub.on('StatsCounterUpdated', (p: any) => {
                if (!p?.counter) return
                setSets((prev) => {
                    if (!prev?.globaux?.length) return prev
                    const row = { ...prev.globaux[0] }
                    if (!(p.counter in row)) return prev
                    row[p.counter] = p.value
                    return { ...prev, globaux: [row, ...prev.globaux.slice(1)] }
                })
            }),
            // Une rafale = 1 seul signal → re-fetch REST complet (vues agrégées)
            statsHub.on('StatsRefreshNeeded', () => LoadData()),
        ]

        return () => {
            unsubs.forEach((u) => u())
            statsHub.release()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Charge les statistiques de la file d'attente selon les filtres (override possible pour le Reset). */
    function LoadData(override?: { filiale?: string; service?: string; canal?: string; debut?: string; fin?: string }) {
        const filiale = override?.filiale ?? filtreFiliale
        const service = override?.service ?? filtreService
        const canal = override?.canal ?? filtreCanal
        const debut = override?.debut ?? dateDebut
        const fin = override?.fin ?? dateFin

        const payload: any = { CodeAbonne: user.CodeAbonne }
        if (filiale) payload.CodeFiliale = filiale
        if (service) payload.CodeS = service
        if (canal) payload.Canal = canal
        if (debut) payload.DateDebut = debut
        if (fin) payload.DateFin = fin

        setLoading(true)
        let config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.Statistiques,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authHeader(),
            },
            data: Crypter.encrypt(JSON.stringify(payload))
        };

        axios.request(config)
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setSets(extractSets(res.Result))
                }
                else {
                    setSets(EMPTY_SETS)
                    alert.warning(res.Message)
                }
            })
            .catch((error: any) => {
                setLoading(false)
                setSets(EMPTY_SETS)
                alert.error('Error', error)
                console.log(error);
            });
    }

    function LoadFiliales() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetAllFiliales + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    setFiliales(Array.isArray(res.Result) ? res.Result : [])
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function LoadServices() {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.GetFilialeServices + Crypter.encrypt(user.CodeAbonne),
            headers: {
                'Accept': 'text/plain',
                'Authorization': authHeader(),
            }
        };

        axios.request(config)
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res.StatutCode == 200) {
                    // GetFilialeServices peut renvoyer [filiales, services] — tolérer aussi un tableau plat [{CodeS,Libelle,CodeFiliale}]
                    if (Array.isArray(res.Result) && Array.isArray(res.Result[0])) {
                        setServices(res.Result[1] || [])
                    } else {
                        setServices(Array.isArray(res.Result) ? res.Result : [])
                    }
                }
            })
            .catch((error: any) => {
                console.log(error);
            });
    }

    function handleReset() {
        setFiltreFiliale('')
        setFiltreService('')
        setFiltreCanal('')
        setDateDebut('')
        setDateFin('')
        LoadData({ filiale: '', service: '', canal: '', debut: '', fin: '' })
    }

    // Les services sont filtrés par la filiale choisie quand l'information est disponible
    const servicesFiltres = services.filter((s: any) =>
        !filtreFiliale || s.CodeFiliale == null || s.CodeFiliale === filtreFiliale
    );

    const counters = sets ? buildCounters(sets.globaux) : []
    const serieData = sets ? buildSerie(sets.serie) : []

    /** Indicateurs documentés : titre, description métier, icône et graphe par défaut. */
    const indicatorDefs: { key: keyof Omit<StatSets, 'extras' | 'globaux' | 'serie'>; title: string; description: string; icon: ReactNode; chart: 'bar' | 'donut' | 'line' }[] = [
        {
            key: 'parService', title: 'Répartition par service', icon: <ConciergeBell size={16} />, chart: 'bar',
            description: 'Volume de tickets par service/guichet — repère les files les plus chargées et aide à rééquilibrer les affectations.',
        },
        {
            key: 'parCanal', title: 'Répartition par canal', icon: <Globe size={16} />, chart: 'donut',
            description: "Origine des tickets (en ligne, borne, mobile, USSD…) — mesure l'adoption des canaux digitaux vs. l'agence.",
        },
        {
            key: 'parStatut', title: 'Répartition par statut', icon: <Activity size={16} />, chart: 'donut',
            description: 'Issue des tickets : validés, rejetés, absents, annulés, transférés. La part des non-validés mesure la qualité de service.',
        },
        {
            key: 'parAgent', title: 'Performance par agent', icon: <Users size={16} />, chart: 'bar',
            description: 'Tickets traités par agent — volumes et efficacité individuelle, utile pour les objectifs et la formation.',
        },
        {
            key: 'tempsMoyens', title: 'Temps moyens', icon: <Timer size={16} />, chart: 'bar',
            description: "Durées moyennes d'attente et de traitement — l'indicateur clé de la fluidité perçue par le client.",
        },
    ]

    const indicators: { title: string; description: string; icon: ReactNode; chart: 'bar' | 'donut' | 'line'; rows: any[] }[] = sets ? [
        ...indicatorDefs.map(d => ({ ...d, rows: (sets as any)[d.key] as any[] })),
        ...sets.extras.map(e => ({
            title: e.name, rows: e.rows, chart: 'bar' as const, icon: <BarChart3 size={16} />,
            description: 'Jeu de données supplémentaire retourné par le serveur.',
        })),
    ] : []

    const hasData = counters.length > 0 || serieData.length > 0 || indicators.some(t => t.rows.length > 0)

    return (
        <Spin spinning={loading} >
            <PageHeader
                icon={<BarChart3 />}
                title="Statistiques"
                subtitle="Activité de la file d'attente : volumes, temps moyens, par agent"
                actions={
                    <Button
                        size='sm'
                        variant="outline"
                        startIcon={<RefreshCw size={15} />}
                        onClick={() => LoadData()}
                        loading={loading}
                    >Actualiser</Button>
                }
            />

            {/* Barre de filtres */}
            <div className="eb-card mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 items-end">
                    <Select
                        label="Filiale"
                        value={filtreFiliale}
                        onChange={setFiltreFiliale}
                        options={[{ value: '', label: 'Toutes les filiales' }, ...filiales.map((f: any) => ({ value: f.CodeFiliale, label: f.CodeFiliale + ' - ' + f.Libelle }))]}
                    />
                    <Select
                        label="Service"
                        value={filtreService}
                        onChange={setFiltreService}
                        options={[{ value: '', label: 'Tous les services' }, ...servicesFiltres.map((s: any) => ({ value: s.CodeS, label: s.CodeS + ' - ' + s.Libelle }))]}
                    />
                    <Select
                        label="Canal"
                        value={filtreCanal}
                        onChange={setFiltreCanal}
                        options={CANAUX}
                    />
                    <Input
                        type='date'
                        label='Date début'
                        value={dateDebut}
                        onChange={(e: any) => setDateDebut(e.target.value)}
                    />
                    <Input
                        type='date'
                        label='Date fin'
                        value={dateFin}
                        onChange={(e: any) => setDateFin(e.target.value)}
                    />
                    <div className="flex gap-2">
                        <Button
                            variant='primary'
                            size='md'
                            startIcon={<Filter size={15} />}
                            onClick={() => LoadData()}
                            loading={loading}
                        >Appliquer</Button>
                        <Button
                            variant='ghost'
                            size='md'
                            onClick={handleReset}
                        >Reset</Button>
                    </div>
                </div>
            </div>

            {!hasData ? (
                <div className="eb-card">
                    <Empty description="Aucune donnée statistique pour les critères sélectionnés" />
                </div>
            ) : (
                <>
                    {/* Compteurs globaux — cliquables pour le détail */}
                    {counters.length > 0 && (
                        <div className="eb-stat-grid">
                            {counters.map(c => {
                                const meta = COUNTER_META[c.key]
                                return (
                                    <div
                                        key={c.key}
                                        onClick={() => setSelectedCounter(c)}
                                        className="cursor-pointer transition-transform hover:-translate-y-0.5"
                                        title="Cliquer pour le détail"
                                    >
                                        <StatCard
                                            label={meta?.label || c.label}
                                            value={formatCounterValue(c)}
                                            icon={meta?.icon || <BarChart3 />}
                                            tone={meta?.tone || 'primary'}
                                        />
                                    </div>
                                )
                            })}
                        </div>
                    )}

                    {/* Série journalière */}
                    {serieData.length > 0 && (
                        <div className="eb-card">
                            <Card style={{ borderRadius: 14 }}>
                                <Title>Activité journalière</Title>
                                <Subtitle>Volume de tickets par jour — tendance d'affluence sur la période filtrée.</Subtitle>
                                <AreaChart
                                    className="mt-6"
                                    data={serieData}
                                    index="Jour"
                                    categories={['Tickets']}
                                    colors={['indigo']}
                                    yAxisWidth={40}
                                />
                            </Card>
                        </div>
                    )}

                    {/* Indicateurs détaillés : graphique <-> données */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {indicators.filter(t => t.rows.length > 0).map(t => (
                            <IndicatorCard
                                key={t.title}
                                title={t.title}
                                description={t.description}
                                icon={t.icon}
                                rows={t.rows}
                                defaultChart={t.chart}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Détail d'un indicateur (clic sur une carte compteur) */}
            <Modal
                open={!!selectedCounter}
                onClose={() => setSelectedCounter(null)}
                title={COUNTER_META[selectedCounter?.key || '']?.label || selectedCounter?.label || 'Indicateur'}
                width={480}
            >
                {selectedCounter && (() => {
                    const meta = COUNTER_META[selectedCounter.key]
                    return (
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-11 h-11 rounded-xl bg-[var(--eb-primary-soft)] text-[var(--eb-primary)] flex items-center justify-center flex-shrink-0">
                                    {meta?.icon || <BarChart3 size={20} />}
                                </span>
                                <div>
                                    <div className="text-2xl font-bold text-[var(--eb-text)]">{formatCounterValue(selectedCounter)}</div>
                                    <div className="text-xs text-[var(--eb-text-muted)] font-mono">{selectedCounter.key}</div>
                                </div>
                            </div>
                            <p className="text-sm text-[var(--eb-text)] leading-relaxed">
                                {meta?.description || 'Indicateur global calculé par le serveur sur la période et le périmètre filtrés.'}
                            </p>
                            <p className="text-[11px] text-[var(--eb-text-muted)] mt-3">
                                Astuce : utilisez les filtres ci-dessus pour analyser cet indicateur par filiale, service, canal ou période.
                            </p>
                        </div>
                    );
                })()}
            </Modal>
        </Spin>
    );
}
